import type { DayType, IsoDate } from '@/shared/types';

/**
 * Calendar-day arithmetic on `YYYY-MM-DD` strings.
 *
 * Everything here treats a date as a label on a wall calendar, never as an
 * instant. We build with `Date.UTC` purely as a proleptic-Gregorian calculator
 * and format back to a string immediately, so no local timezone offset can
 * shift a day boundary.
 */

const DAY_MS = 86_400_000;

const WEEKDAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

export type Weekday = (typeof WEEKDAY_NAMES)[number];

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// Returns a plain boolean rather than a type predicate: `IsoDate` is an alias
// for `string`, so narrowing would collapse the false branch to `never`.
export function isIsoDate(value: string): boolean {
  if (!ISO_DATE_RE.test(value)) return false;
  return toEpochDay(value) !== null;
}

/** Days since the Unix epoch, or null if the string is not a real date. */
function toEpochDay(date: IsoDate): number | null {
  const year = Number(date.slice(0, 4));
  const month = Number(date.slice(5, 7));
  const day = Number(date.slice(8, 10));
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const ms = Date.UTC(year, month - 1, day);
  const d = new Date(ms);
  // Rejects things like 2026-02-30, which Date.UTC would happily roll over.
  if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day) {
    return null;
  }
  return ms / DAY_MS;
}

function epochDay(date: IsoDate): number {
  const value = toEpochDay(date);
  if (value === null) throw new RangeError(`Not a valid date: ${date}`);
  return value;
}

function fromEpochDay(days: number): IsoDate {
  return new Date(days * DAY_MS).toISOString().slice(0, 10);
}

export function addDays(date: IsoDate, days: number): IsoDate {
  return fromEpochDay(epochDay(date) + days);
}

export function addWeeks(date: IsoDate, weeks: number): IsoDate {
  return addDays(date, weeks * 7);
}

/** Whole days from `a` to `b`. Negative when `b` is earlier. */
export function daysBetween(a: IsoDate, b: IsoDate): number {
  return epochDay(b) - epochDay(a);
}

export function compareDates(a: IsoDate, b: IsoDate): number {
  // ISO dates are zero-padded, so lexical order is chronological order.
  return a < b ? -1 : a > b ? 1 : 0;
}

export function minDate(a: IsoDate, b: IsoDate): IsoDate {
  return a <= b ? a : b;
}

export function maxDate(a: IsoDate, b: IsoDate): IsoDate {
  return a >= b ? a : b;
}

export function weekdayOf(date: IsoDate): Weekday {
  // 1970-01-01 was a Thursday (index 4).
  const index = (((epochDay(date) + 4) % 7) + 7) % 7;
  return WEEKDAY_NAMES[index]!;
}

/** The meeting day type for a date, or null if it isn't a meeting day. */
export function dayTypeOf(date: IsoDate, meetingDays: readonly DayType[]): DayType | null {
  const weekday = weekdayOf(date);
  return meetingDays.find((d) => d === weekday) ?? null;
}

/** Every meeting date in `[start, end]`, ascending. */
export function meetingDatesBetween(
  start: IsoDate,
  end: IsoDate,
  meetingDays: readonly DayType[],
): IsoDate[] {
  if (meetingDays.length === 0 || compareDates(start, end) > 0) return [];
  const dates: IsoDate[] = [];
  const last = epochDay(end);
  for (let day = epochDay(start); day <= last; day++) {
    const date = fromEpochDay(day);
    if (dayTypeOf(date, meetingDays)) dates.push(date);
  }
  return dates;
}

/** Today in a given IANA timezone, as a calendar date. */
export function todayInTimezone(timezone: string, now: Date = new Date()): IsoDate {
  try {
    // en-CA formats as YYYY-MM-DD, which is exactly our storage format.
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(now);
  } catch {
    return now.toISOString().slice(0, 10);
  }
}

/** e.g. "Thu, Aug 20". Used everywhere in the UI. */
export function formatShort(date: IsoDate): string {
  const [y, m, d] = [date.slice(0, 4), date.slice(5, 7), date.slice(8, 10)];
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${y}-${m}-${d}T00:00:00Z`));
}

/** e.g. "Aug 20". Used where the weekday is already shown alongside. */
export function formatDayMonth(date: IsoDate): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${date}T00:00:00Z`));
}

/** "19:00" as "7:00 PM", for anywhere a person reads the time rather than types it. */
export function formatTime(time: string): string {
  const [hh = '0', mm = '00'] = time.split(':');
  const hour = Number(hh);
  if (!Number.isFinite(hour)) return time;
  const suffix = hour < 12 ? 'AM' : 'PM';
  const twelve = hour % 12 === 0 ? 12 : hour % 12;
  return `${twelve}:${mm} ${suffix}`;
}

/** e.g. "Thursday, August 20, 2026". */
export function formatLong(date: IsoDate): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${date}T00:00:00Z`));
}

/**
 * RFC3339 timestamp for a local wall-clock time on a date in a timezone.
 * Google Calendar accepts a naive local time paired with an explicit
 * `timeZone` field, which sidesteps offset math entirely.
 */
export function localDateTime(date: IsoDate, time: string): string {
  const [hh = '00', mm = '00'] = time.split(':');
  return `${date}T${hh.padStart(2, '0')}:${mm.padStart(2, '0')}:00`;
}

export function addMinutesToTime(time: string, minutes: number): string {
  const [hh = '0', mm = '0'] = time.split(':');
  const total = Number(hh) * 60 + Number(mm) + minutes;
  const wrapped = ((total % 1440) + 1440) % 1440;
  const h = Math.floor(wrapped / 60);
  const m = wrapped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** True when adding `minutes` to `time` rolls past midnight. */
export function crossesMidnight(time: string, minutes: number): boolean {
  const [hh = '0', mm = '0'] = time.split(':');
  return Number(hh) * 60 + Number(mm) + minutes >= 1440;
}
